import {
  MAX_HEIGHT,
  MAX_WEIGHT,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import {

  Auction,
  Careseeker,
  GetOntologiesType,
} from "core/types";
import { Iterable } from "immutable";
import { composeValidation, required as formRequired } from "react-forms-state";
import Translations from "translations/types";
import {
  ConversionJob,
  ElementOptions,
  ElementOptionsT,
  MultiSelectElementOptions,
  WhitelistElementOptions,
  WhitelistOption,
} from "./types";

export function toJS<T>(
  maybeIterable: Parameters<typeof Iterable.isIterable>[0],
): T {
  return Iterable.isIterable(maybeIterable)
    ? maybeIterable.toJS()
    : maybeIterable;
}

export const emailRegex =
  /^[\w.&+-]+@[a-zA-Z\d](?:[a-zA-Z\d-]*[a-zA-Z\d])?(?:\.[a-zA-Z\d](?:[a-zA-Z\d-]*[a-zA-Z\d])?)*\.[a-zA-Z]{2,}$/;

const checkForTranslationsProp = (props: Record<string, any>) =>
  props?.translations != null;

export const containsLink = (str: string) =>
  /(?:https?|ftp):\/\/[\n\S]+/.test(str);

export const validateEmail = ({
  allowSpecialChars,
  email,
  required,
}: {
  allowSpecialChars?: boolean;
  email: string | null;
  required?: boolean;
}) => {
  if (!email) return required ? "empty" : true;
  const trimmed = email.trim();

  // checks for anything@domain.extension
  if (!/.*@.*\..*/.test(trimmed)) return "wrongform";
  if (trimmed.indexOf(",") > -1) return "invalidcharacter";

  // prevents two @
  if (trimmed.indexOf("@", trimmed.indexOf("@") + 1) > -1) return "twoats";

  if (!allowSpecialChars && /.*(ü|ä|ö|ß).*/.test(trimmed))
    return "invalidcharacter";
  return true;
};

export function emailValidation({
  allowSpecialChars,
  customTranslation,
  required,
}: {
  allowSpecialChars?: boolean;
  customTranslation?: (t: Translations) => string;
  required: boolean;
}) {
  return (
    email: string | null,
    props: Record<string, any>,
  ): boolean | { customMessage: string } => {
    const isValid = validateEmail({ allowSpecialChars, email, required });
    if (isValid === true) return true;
    return checkForTranslationsProp(props)
      ? {
          customMessage:
            customTranslation?.(props.translations) ||
            props.translations.people.invalidEmailAddress,
        }
      : false;
  };
}

export const emailModel = ({
  allowSpecialChars,
  customTranslation,
  fieldRequired,
}: {
  allowSpecialChars?: boolean;
  customTranslation?: (t: Translations) => string;
  fieldRequired: boolean;
}): ElementOptions => ({
  convertOut: (value: any) => (value ? value.trim() : value),
  fieldRequired,
  validate: emailValidation({
    allowSpecialChars,
    customTranslation,
    required: fieldRequired,
  }),
});

export function processPhoneNumber(number: string | undefined) {
  if (!number) return number;
  return number.replace(/[\s().\\/-]/g, "");
}

function removeEmptyCharacters(str: string): string {
  return str.replace(/\s+/g, "");
}

export function validatePhoneNumber(required: boolean) {
  return (
    phoneNumber: string | null | undefined,
    props: { translations: Translations },
  ): boolean | { customMessage: string } => {
    const errorResponse = checkForTranslationsProp(props)
      ? { customMessage: props.translations.people.invalidPhoneNumber }
      : false;

    const trimmedPhoneNumber = phoneNumber
      ? removeEmptyCharacters(phoneNumber)
      : "";

    if (trimmedPhoneNumber === "") {
      return required ? errorResponse : true;
    }

    const isValid = /^(\+?[0-9])[0-9\-/]*$/.test(trimmedPhoneNumber);

    return isValid ? true : errorResponse;
  };
}

export const phoneNumberModel = (required: boolean): ElementOptions => ({
  convertOut: (value) => (value ? processPhoneNumber(value) : value),
  fieldRequired: required,
  validate: (value, props) => validatePhoneNumber(required)(value, props),
});

export const requiredValidation =
  (options?: ElementOptions) => (value: any, props: Record<string, any>) => {
    if (!options?.fieldRequired) return true;
    return formRequired({
      errorString: options?.errorString?.(props.translations),
    })(value, props);
  };

export function requiredValidationT<T, FormInputValueType>(
  options?: ElementOptionsT<T, FormInputValueType>,
) {
  return (value: T, props: Record<string, any>) => {
    if (!options?.fieldRequired) return true;
    return formRequired({
      errorString: options?.errorString?.(props.translations),
    })(value, props);
  };
}

const linkValidation = () => (value: any, props: Record<string, any>) =>
  containsLink(value) ? props.translations.general.containsLink : true;

export const convertEncryptedFieldOut = (value: string | null) => {
  if (!value) return null;

  return { decrypted: value };
};

export const convertEncryptedFieldIn = (value: Record<string, any>) => {
  if (!value) return null;

  const decrypted = Iterable.isIterable(value)
    ? value.get("decrypted")
    : value.decrypted;

  if (!decrypted) return "";

  return decrypted;
};

export function validateHouseNumber(
  value: string | null | undefined,
  props: { translations: Translations },
): boolean | { customMessage: string } {
  if (!value || value.trim() === "") {
    return true;
  }

  const trimmedValue = value.trim();
  const isValid = /^\d+[A-Za-z]?(-\d+)?(\/\d+[A-Za-z]?)?$/.test(trimmedValue);

  return isValid
    ? true
    : {
        customMessage: props.translations.address.invalidHouseNumber,
      };
}

function getValidate(options?: ElementOptions) {
  return (
    values: any,
    formState: {
      careseeker?: Careseeker;
      formInputValue: Auction;
      translations: Translations;
    },
    globalValue: Iterable<any, any> | null,
  ) => {
    // Bypass the validation when the field should not be displayed

    if (formState && options?.show?.(formState) === false) return true;

    if (options?.fieldRequired && options.validate)
      return composeValidation(requiredValidation(options), options?.validate)(
        values,
        formState,
      );

    if (options?.fieldRequired)
      return requiredValidation(options)(values, formState);

    if (options?.validate)
      return options.validate(values, formState, globalValue);

    return true;
  };
}

function getConvertOut(options?: ElementOptions) {
  return (
    value: any,
    props: Record<string, any> & { formInputValue: Auction },
    formOutputValue: any,
  ) => {
    // This breaks our current forms where fields can be displayed in two places.
    // This might break a bit the solution change.
    // if (
    //   options &&
    //   options.show &&
    //   props.formInputValue &&
    //   options.show(props) === false
    // )
    //   return null;
    if (options?.convertOut)
      return options.convertOut(value, props, formOutputValue);

    return value;
  };
}

function getShow(
  name: string,
  options: ElementOptions | WhitelistElementOptions | undefined,
) {
  if (options?.show) return options.show;

  const elementName = options?.out_name || name;

  if (options?.whitelist) return () => !!options?.whitelist?.[elementName];

  return undefined;
}

export const encryptedDef = <T extends string>(
  name: T,
  options?: ElementOptions,
): Record<T, ConversionJob> => {
  const show = getShow(name, options);
  options = { ...options, show };
  const validate = getValidate(options);

  return Object.assign({
    [name]: {
      convertIn: options?.convertIn || convertEncryptedFieldIn,
      convertOut: options?.convertOut || convertEncryptedFieldOut,
      default: options?.defaultValue ?? null,
      out: options?.out_name || name,
      show,
      validate,
    },
  });
};

export const whitelistEncryptedDef = <T extends string>(
  name: T,
  options: WhitelistElementOptions,
) => encryptedDef<T>(name, options);

export function valueDef<T extends string>(
  name: T,
  options?: ElementOptions,
): Record<T, ConversionJob> {
  const show = getShow(name, options);
  options = { ...options, show };
  const validate = getValidate(options);

  return {
    [name]: {
      convertIn: options?.convertIn,
      convertOut: getConvertOut(options),
      default: options?.defaultValue ?? null,
      errorString: options.errorString,
      out: options?.out_name || name,
      show,
      validate,
    },
  } as unknown as Record<T, ConversionJob>;
}

export function whitelistValueDef<T extends string>(
  name: T,
  options: WhitelistElementOptions,
) {
  return valueDef<T>(name, options);
}

export function groupDef<T extends string>(
  name: T,
  children: Record<string, any>,
): Record<T, ConversionJob> {
  return Object.assign({
    [name]: {
      show: getShow(name, {
        show: children.show,
        whitelist: children.whitelist,
      }),
      ...valueDef(name, {
        show: getShow(name, {
          show: children.show,
          whitelist: children.whitelist,
        }),
      }),
      ...children,
    },
  });
}

export function whitelistGroupDef<T extends string>(
  name: T,
  children: Record<string, any> & WhitelistOption,
) {
  return groupDef<T>(name, children);
}

export const textValueDef = <T extends string>(
  name: T,
  options?: ElementOptions,
): Record<T, ConversionJob> => {
  return Object.assign({
    [name]: {
      convertIn: options?.convertIn,
      convertOut: getConvertOut(options),
      default: options?.defaultValue ?? null,
      out: options?.out_name || name,
      validate: composeValidation(
        requiredValidation(options),
        linkValidation(),
      ),
    },
  });
};

export const listDef = <T extends string>(
  name: T,
  options?: ElementOptions,
): Record<T, ConversionJob> => {
  const show = getShow(name, options);
  options = { ...options, show };
  return Object.assign({
    [name]: {
      convertIn: options?.convertIn,
      convertOut: options?.convertOut,
      default: options?.defaultValue || null,
      out: name,
      show,
      validate: options?.fieldRequired
        ? (value: any, props: Record<string, any>) =>
            value && value.size > 0 ? true : props.translations.actions.missing
        : null,
    },
  });
};

export const whitelistListDef = <T extends string>(
  name: T,
  options: WhitelistElementOptions,
) => listDef<T>(name, options);

export function algoliaReactSelectDefT<FormInputValueType>(
  name: string,
  options?: ElementOptionsT<any, FormInputValueType>,
) {
  const convertIn = (value: any) => {
    const formValue = Iterable.isIterable(value) ? value.toJS() : value;
    return formValue?.name && formValue.id
      ? {
          label: formValue?.name,
          value: formValue?.id,
        }
      : null;
  };
  const convertOut = (value: any) => {
    const formValue = Iterable.isIterable(value) ? value.toJS() : value;
    return {
      id: formValue?.value,
      name: formValue?.label,
    };
  };

  // @ts-ignore
  const show = getShow(name, options);
  // @ts-ignore
  options = { ...options, show };
  const validate = options?.fieldRequired
    ? composeValidation(
        requiredValidationT<any, FormInputValueType>(options),
        options?.validate || (() => true),
      )
    : options?.validate || (() => true);

  return Object.assign({
    [name]: {
      convertIn: options?.convertIn || convertIn,
      convertOut: options?.convertOut || convertOut,
      default: options?.defaultValue ?? null,
      out: options?.out_name || name,
      show,
      validate,
    },
  });
}

export const algoliaReactSelectDef = (name: string, options?: ElementOptions) =>
  algoliaReactSelectDefT<Auction>(name, options);

export function mapToValues(value: any) {
  if (Array.isArray(value)) {
    return value.map((selected) => selected.value);
  }
  return [];
}

export function multiReactSelectDef<T extends string>(
  name: T,
  { mapToValuesArray = true, ...options }: MultiSelectElementOptions = {
    mapToValuesArray: true,
  },
): Record<T, ConversionJob> {
  const convertIn = (
    input: any,
    props: { getOntologies: GetOntologiesType },
  ) => {
    const { getOntologies } = props;
    let inputValue = Iterable.isIterable(input) ? input.toJS() : input;
    if (!inputValue?.length && options?.defaultValue) {
      inputValue = options.defaultValue;
    }
    if (inputValue && options?.ontology) {
      const ontologyValues = getOntologies({
        sort: true,
        type: options.ontology,
      });
      return ontologyValues
        .filter((ontVal) => inputValue.includes(ontVal.value))
        .map(({ label, value }) => ({ label, value }));
    }
    if (
      Array.isArray(inputValue) &&
      inputValue.length > 0 &&
      // is this an array of primitive values
      Object(inputValue[0]) !== inputValue[0]
    )
      return inputValue.map((value) => ({ label: value, value }));

    return inputValue || [];
  };

  const convertOut = (value: Record<string, any>) => {
    const outputValue = Iterable.isIterable(value) ? value.toJS() : value;

    let converted = Array.isArray(outputValue)
      ? outputValue
      : outputValue?.value;

    if (mapToValuesArray) {
      converted = mapToValues(converted);
    }
    if (options?.convertOut) {
      return options.convertOut(converted);
    }

    return converted;
  };

  const validate = options?.validate
    ? options.validate
    : options?.fieldRequired
    ? (value: any, props: Record<string, any>) =>
        value && value.length > 0 ? true : props.translations.actions.missing
    : null;

  return Object.assign({
    [name]: {
      convertIn: options?.convertIn || convertIn,
      convertOut,
      out: name,
      validate,
    },
  });
}

// watchOutPath should follow the conversion model's `out` hierarchy and not `in`
export function convertOutAssertPath(
  watchOutPath: string,
  defaultValue: any,
  expectedWatchValue?: any,
) {
  const expected = expectedWatchValue || true;
  return (value: any, props?: Record<string, any>, allValues?: any) => {
    if (allValues?.getIn !== undefined) {
      if (allValues.getIn(watchOutPath.split(".")) !== expected) {
        return defaultValue;
      }
    }
    return value;
  };
}

export function getPlaceholderField(field: string) {
  return `_${field}`;
}

// populate placeholder _field's boolean value for ActivableInput
export const valueDefActivableInput = (
  fullPath: string,
  options: {
    defaultValue?: boolean;
    expectedWatchValue?: any;
    show?: (p: any) => boolean;
  } = {},
) => {
  const { defaultValue, expectedWatchValue, show } = options;
  const pathArr = fullPath.split(".");
  const name = pathArr[pathArr.length - 1];
  return {
    ...valueDef(getPlaceholderField(name), {
      convertIn: (value: any, props: Record<string, any>, allValues: any) => {
        if (allValues?.getIn !== undefined) {
          const selected = defaultValue || allValues.getIn(pathArr) != null;

          if (expectedWatchValue == 1) {
            return selected ? 1 : 0;
          }

          return selected;
        }
        return false;
      },
      show,
    }),
    ...valueDef(name, {
      convertOut: convertOutAssertPath(
        getPlaceholderField(name),
        null,
        expectedWatchValue,
      ),
    }),
  };
};

export const activableInputModelDefinition = <T extends string>(
  // Full path should be the path of the struct in our graphql model (what the backend sends)
  fullPath: string,
  // Form element name is the element name of the struct in the form.
  formElementName: T,
  children: Record<string, any>,
  options: {
    defaultValue?: boolean;
    expectedWatchValue?: any;
  } = {},
): Record<T, ConversionJob> => {
  const { defaultValue, expectedWatchValue } = options;
  const expected = expectedWatchValue || true;
  const show = getShow(formElementName, {
    show: children.show,
    whitelist: children.whitelist,
  });
  return Object.assign({
    ...valueDefActivableInput(fullPath, {
      defaultValue,
      expectedWatchValue,
      show,
    }),
    [formElementName]: {
      convertOut: convertOutAssertPath(
        getPlaceholderField(formElementName),
        null,
        expected,
      ),
      ...(show ? valueDef(formElementName, { show }) : {}),
      ...children,
    },
  });
};

export const whitelistActivableInputModelDefinition = <T extends string>(
  fullPath: string,
  formElementName: T,
  children: Record<string, any> & WhitelistOption,
  options: { defaultValue?: boolean; expectedWatchValue?: any } = {},
) =>
  activableInputModelDefinition<T>(
    fullPath,
    formElementName,
    children,
    options,
  );

export const getPredicamentOptions = (translations: Translations) => [
  { id: "yes", label: translations.actions.yes, value: PREDICAMENT_STATE_YES },
  { id: "no", label: translations.general.no, value: PREDICAMENT_STATE_NO },
  {
    id: "unknown",
    label: translations.general.unknown,
    value: PREDICAMENT_STATE_UNKNOWN,
  },
];

export const getPredicamentValue = ({
  label,
  translations,
  value,
}: {
  label?: string;
  translations: Translations;
  value: number | null | undefined;
}) => {
  const prefix = label ? `${label}${translations.general.colon} ` : "";
  switch (value) {
    case PREDICAMENT_STATE_YES:
      return `${prefix}${translations.actions.yes}`;
    case PREDICAMENT_STATE_NO:
      return `${prefix}${translations.general.no}`;
    case PREDICAMENT_STATE_UNKNOWN:
      return `${prefix}${translations.general.unknown}`;
    default:
      return "";
  }
};

export const validateIBAN = (value: any, props: any) => {
  if (!value) return true;
  if (!/^DE([0-9a-zA-Z]\s?){20}$/.test(value)) {
    return {
      customMessage:
        props.translations.patient.formGeneration.patientBankIbanInvalid,
    };
  }

  return true;
};

export const ibanDef = (name: string) =>
  valueDef(name, {
    validate: validateIBAN,
  });

export function validateNumberIsInRange({
  maxValue,
  minValue = 0,
  props,
  value,
}: {
  maxValue: number;
  minValue?: number;
  props: any;
  value: string | null | undefined;
}): boolean | { customMessage: string } {
  if (!value) return true;

  const numericValue = Number(value);

  if (
    isNaN(numericValue) ||
    numericValue < minValue ||
    numericValue > maxValue
  ) {
    return {
      customMessage: props.translations.actions.invalid,
    };
  }

  return true;
}

export function validateHeight(
  value: string | null | undefined,
  props: any,
): boolean | { customMessage: string } {
  return validateNumberIsInRange({ maxValue: MAX_HEIGHT, props, value });
}

export function validateWeight(
  value: string | null | undefined,
  props: any,
): boolean | { customMessage: string } {
  return validateNumberIsInRange({ maxValue: MAX_WEIGHT, props, value });
}

export function emailDef(
  elementName: string,
  props: {
    allowSpecialChars?: boolean;
    customTranslation?: (t: Translations) => string;
    fieldRequired: boolean;
  },
) {
  return valueDef(elementName, emailModel(props));
}
